import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Collapse, Row } from 'reactstrap';

import * as CUI from '@CUI';
import { useActions } from '@common/store-utils/hooks';
import {
  CheckBox,
  CollapseHeader,
  LabelWithHelp,
  LabelWithPopup,
  OptionsInput,
} from '@common/perach-ui';

import {
  DicRecord,
  dictionariesModel,
  fixValue,
} from '@entities/dictionaries';
import { RegistrationInputField } from './RegistrationInputField';
import { actions, selectors } from '../model';
import {
  Address,
  DegreeType,
  EducationDetails,
  MasterTrackType,
  PersonalInfo,
  StudyStartSemester,
} from '../types';
import { personalInfoDefaultValues } from '../defaults';
import { config } from '../config';
import { stepOneFields } from '../fields';
import { Optional } from '@common/ui';
import { subtractYears } from '@features/Registration/utils';
import { HelpKeyType, HelpText } from '@entities/settings';
import { userModel } from '@entities/user';

const InputField: React.FC<{ label: string } & CUI.InputTypes.InputProps> = ({
  label,
  ...props
}) => {
  const { t } = useTranslation();
  return (
    <RegistrationInputField
      form={config.forms.personalData}
      label={label}
      requiredMessage={t('FIELD_REQUIRED')}
      {...props}
    />
  );
};

/**
 * Users personal data for Registration
 * @returns React.FC
 * */
export const PersonalInfoForm: React.FC<{
  disabledFields?: Record<string, boolean>;
  shouldHideButtons?: boolean;
}> = ({
  disabledFields = { userId: true, phone: true, school: true },
  shouldHideButtons = false,
}) => {
  const { t } = useTranslation();
  const { updatePersonalInfo, saveUserPersonalData, setIsModalOpen } =
    useActions(actions);
  // const { getDistricts } = useActions(dictionariesModel.actions);
  const personalInfo =
    selectors.usePersonalInfoData() || personalInfoDefaultValues;
  const cities = dictionariesModel.selectors.useCities();
  const studyFields = dictionariesModel.selectors.useStudyFields();
  // const districts = dictionariesModel.selectors.useDistricts();
  // const loaders = dictionariesModel.selectors.useLoaders();
  const isAcademicsReligious = userModel.selectors.useIsAcademicsReligious();

  const GENDER_OPTIONS = [
    { value: 'MALE', label: t('MALE') },
    { value: 'FEMALE', label: t('FEMALE') },
    { value: 'ANOTHER', label: t('OTHER') },
  ];

  const BOOL_OPTIONS = [
    { value: true, label: t('YES') },
    { value: false, label: t('NO') },
  ];

  const initialCollapses = {
    1: true,
    2: true,
    3: true,
    4: true,
    5: true,
    6: true,
    7: true,
  };

  const [collapses, setCollapses] =
    useState<Record<string, boolean>>(initialCollapses);

  const toggleCollapse = (id: string) =>
    setCollapses({ ...collapses, [id]: !collapses[id] });

  const onFieldChange = (e: CUI.InputTypes.ChangeEvent) => {
    const { name, value } = e.component;
    updatePersonalInfo({
      changes: { [name as Partial<keyof PersonalInfo>]: value },
    });
  };

  const handleChange = (field: keyof PersonalInfo, value: string | boolean) =>
    updatePersonalInfo({ changes: { [field]: value } });

  const handleChangeAddress = (
    type: keyof Pick<PersonalInfo, 'addressById' | 'addressWhileStudying'>,
    field: keyof Address,
    value: string | boolean | { value?: any; text?: string }
  ) =>
    updatePersonalInfo({
      changes: {
        [type]: { ...personalInfo[type], [field]: value },
        addressesMatch:
          type === 'addressWhileStudying'
            ? false
            : personalInfo?.addressesMatch,
      },
    });

  const handleChangeEducation = (
    field: keyof EducationDetails,
    value: string | boolean | DicRecord
  ) =>
    updatePersonalInfo({
      changes: {
        educationDetails: {
          ...personalInfo['educationDetails'],
          [field]: value,
        },
      },
    });

  useEffect(() => {
    if (personalInfo.addressesMatch) {
      updatePersonalInfo({
        changes: { addressWhileStudying: personalInfo.addressById },
      });
    }
  }, [
    personalInfo.addressesMatch,
    personalInfo.addressWhileStudying,
    personalInfo.addressById,
  ]);

  const sectorMock = [
    { value: '1', text: 'מגזר חרדי' },
    { value: '2', text: 'חברה ערבית' },
    { value: '3', text: 'יוצאי אתיופיה' },
  ];

  const schoolYearMock = [
    { value: 'FIRST_YEAR', text: 'שנה ראשונה' },
    { value: 'SECOND_YEAR', text: 'שנה שנייה' },
    { value: 'THIRD_YEAR', text: 'שנה שלישית' },
    { value: 'FOUR_YEAR', text: 'שנה רביעית' },
    { value: 'FIVE_PLUS_YEAR', text: 'שנה חמישית +' },
  ];

  const degreeTypeMock = [
    { value: DegreeType.BACHELOR, text: t('DEGREE_TYPE_BACHELOR') },
    { value: DegreeType.MASTER, text: t('DEGREE_TYPE_MASTER') },
  ];

  const studyStartSemesterMock = [
    { value: StudyStartSemester.SEMESTER_A, text: t('STUDY_START_SEMESTER_A') },
    { value: StudyStartSemester.SEMESTER_B, text: t('STUDY_START_SEMESTER_B') },
  ];

  const masterTrackMock = [
    { value: MasterTrackType.RESEARCH, text: t('MASTER_TRACK_RESEARCH') },
    { value: MasterTrackType.NON_RESEARCH, text: t('MASTER_TRACK_NON_RESEARCH') },
  ];

  // useEffect(() => {
  //   if (!!personalInfo?.under18City?.value) {
  //     getDistricts(personalInfo?.under18City?.value);
  //   }
  // }, [personalInfo?.under18City]);

  // const isButtonDisabled = !personalInfo?.neighborhoodConfirmCheckbox; // !personalInfo?.under18Confirm;

  // @ts-ignore
  return (
    <>
      <div className="form-container ph-25">
        <CollapseHeader
          id="1"
          isOpen={collapses['1']}
          title={t('PERSONAL_INFO')}
          toggle={() => toggleCollapse('1')}
        />
        <Collapse isOpen={collapses['1']}>
          <Row className="g-2 gx-5">
            <Col className="col-12 col-md-6">
              <InputField
                label={t('FIRST_NAME')}
                name={stepOneFields.firstName}
                isDisabled={!!disabledFields?.[stepOneFields.firstName]}
                value={personalInfo.firstName}
                onChange={onFieldChange}
                isRequired
                maxLength={20}
              />
            </Col>
            <Col className="col-12 col-md-6">
              <InputField
                label={t('LAST_NAME')}
                name={stepOneFields.lastName}
                isDisabled={!!disabledFields?.[stepOneFields.lastName]}
                value={personalInfo.lastName}
                onChange={onFieldChange}
                forbiddenSymbols={'numbers'}
                isRequired
                maxLength={20}
              />
            </Col>
          </Row>
          <Row className="g-2 gx-5">
            <Col className="col-12 col-md-6">
              <InputField
                label={t('IDENTITY_NUMBER')}
                name={stepOneFields.userId}
                isDisabled={!!disabledFields?.[stepOneFields.userId]}
                value={personalInfo.userId}
                onChange={onFieldChange}
                validator={'israelId'}
                maxLength={9}
                minLength={7}
                invalidMessage={t('ENTER_CORRECT_ID')}
                isRequired
              />
            </Col>
            <Col className="col-12 col-md-6">
              <LabelWithPopup
                label={t('BIRTHDAY')}
                helpKey={stepOneFields.birthday}
              />
              <CUI.DatePicker
                _wideList
                form={config.forms.personalData}
                name={stepOneFields.birthday}
                isDisabled={!!disabledFields?.[stepOneFields.birthday]}
                placeholder={' '}
                value={personalInfo?.birthday || null}
                format={''}
                max={subtractYears(8)}
                min={new Date('01-01-1901')}
                onChange={(e) => {
                  updatePersonalInfo({
                    changes: {
                      birthday: e.component.value || null,
                    },
                  });
                }}
                isRequired
                requiredMessage={t('FIELD_REQUIRED')}
              />
            </Col>
          </Row>
          <Row className="g-2 gx-5">
            <Col className="col-12 mb-40">
              <InputField
                label={t('BIRTHDAY_HEBREW')}
                name={stepOneFields.birthdayHebrew}
                isDisabled={!!disabledFields?.[stepOneFields.birthdayHebrew]}
                value={personalInfo.birthdayHebrew}
                onChange={onFieldChange}
              />
            </Col>
          </Row>
          <Row className="g-2 gx-5">
            <Col className="col-12 col-md-6 mb-40">
              <LabelWithPopup
                label={t('GENDER')}
                helpKey={stepOneFields.gender}
              />
              <OptionsInput
                isDisabled={!!disabledFields?.[stepOneFields.gender]}
                options={GENDER_OPTIONS}
                value={personalInfo?.gender || ''}
                onChange={(val) =>
                  updatePersonalInfo({ changes: { gender: val } })
                }
              />
            </Col>
            <Optional
              when={!!isAcademicsReligious && personalInfo?.gender === 'FEMALE'}
            >
              <Col className="col-12 col-md-6 mb-40">
                <LabelWithPopup
                  label={'האם הינך מהמגזר החרדי?'}
                  helpKey={stepOneFields.isReligiousSector}
                />
                <OptionsInput
                  isDisabled={
                    !!disabledFields?.[stepOneFields.isReligiousSector]
                  }
                  options={BOOL_OPTIONS}
                  value={personalInfo?.isReligiousSector || false}
                  onChange={(val) =>
                    updatePersonalInfo({ changes: { isReligiousSector: val } })
                  }
                />
              </Col>
            </Optional>
            {/*TODO: Delete after fixes */}
            {/*<Col className="col-12 col-md-6 mb-40">*/}
            {/*  <CUI.Label _labelPerach>{t('SECTOR')}</CUI.Label>*/}
            {/*  <CUI.DropDownSelect*/}
            {/*    form={config.forms.personalData}*/}
            {/*    name={stepOneFields.sector}*/}
            {/*    isDisabled={!!disabledFields?.[stepOneFields.sector]}*/}
            {/*    value={fixValue(personalInfo?.sector, sectors)}*/}
            {/*    data={sectors}*/}
            {/*    textField={'text'}*/}
            {/*    shouldAllowEmpty*/}
            {/*    shouldFilterValues*/}
            {/*    onChange={(e) =>*/}
            {/*      updatePersonalInfo({*/}
            {/*        changes: { sector: e.component.value },*/}
            {/*      })*/}
            {/*    }*/}
            {/*    isRequired*/}
            {/*    requiredMessage={t('FIELD_REQUIRED')}*/}
            {/*  />*/}
            {/*</Col>*/}
          </Row>
        </Collapse>

        <CollapseHeader
          id="2"
          isOpen={collapses['2']}
          title={t('CONNECT_INFO')}
          toggle={() => toggleCollapse('2')}
        />
        <Collapse isOpen={collapses['2']}>
          <Row className="g-2 gx-5">
            <Col className="col-12 col-md-6">
              <InputField
                label={t('PHONE')}
                name={stepOneFields.phone}
                isDisabled={!!disabledFields?.[stepOneFields.phone]}
                value={personalInfo.phone}
                onChange={onFieldChange}
                allowedSymbols="numbers"
                isRequired
                maxLength={10}
                minLength={9}
                validator={(v) => v.length >= 9 && v.length <= 10}
                invalidMessage={t('ENTER_CORRECT_PHONE')}
              />
            </Col>
            <Col className="col-12 col-md-6">
              <InputField
                label={t('ADDITIONAL_PHONE')}
                name={stepOneFields.phoneAdditional}
                isDisabled={!!disabledFields?.[stepOneFields.phoneAdditional]}
                value={personalInfo.phoneAdditional}
                allowedSymbols="numbers"
                onChange={onFieldChange}
                maxLength={10}
                minLength={9}
                validator={(v) => v.length >= 9 && v.length <= 10}
                invalidMessage={t('ENTER_CORRECT_PHONE')}
              />
            </Col>
          </Row>
          <Row className="g-2 gx-5">
            <Col className="col-12 col-md-6">
              <InputField
                label={t('EMAIL_ADDRESS')}
                name={stepOneFields.email}
                isDisabled
                value={personalInfo.email}
              />
            </Col>
          </Row>
        </Collapse>

        {/*<CollapseHeader*/}
        {/*  id="3"*/}
        {/*  isOpen={collapses['3']}*/}
        {/*  title={t('ADDRESS_BY_ID')}*/}
        {/*  toggle={() => toggleCollapse('3')}*/}
        {/*/>*/}
        {/*<Collapse isOpen={collapses['3']}>*/}
        {/*  <Row className="g-2 gx-5">*/}
        {/*    <Col className="col-12 col-md-6 mb-40">*/}
        {/*      <CUI.Label _labelPerach>{t('CITY')}</CUI.Label>*/}
        {/*      <CUI.DropDownSelect*/}
        {/*        form={config.forms.personalData}*/}
        {/*        name={stepOneFields.addressById}*/}
        {/*        isDisabled={!!disabledFields?.[stepOneFields.addressById]}*/}
        {/*        value={fixValue(personalInfo?.addressById?.city, cities)}*/}
        {/*        data={cities}*/}
        {/*        textField={'text'}*/}
        {/*        shouldAllowEmpty={false}*/}
        {/*        shouldFilterValues*/}
        {/*        onChange={(e) =>*/}
        {/*          handleChangeAddress(*/}
        {/*            'addressById',*/}
        {/*            'city',*/}
        {/*            e?.component?.value*/}
        {/*          )*/}
        {/*        }*/}
        {/*        isRequired*/}
        {/*        requiredMessage={t('FIELD_REQUIRED')}*/}
        {/*      />*/}
        {/*    </Col>*/}
        {/*    <Col className="col-12 col-md-6">*/}
        {/*      <InputField*/}
        {/*        label={t('STREET')}*/}
        {/*        name={stepOneFields.street}*/}
        {/*        isDisabled={!!disabledFields?.[stepOneFields.street]}*/}
        {/*        value={personalInfo.addressById.street}*/}
        {/*        isRequired*/}
        {/*        maxLength={30}*/}
        {/*        onChange={(e) =>*/}
        {/*          handleChangeAddress(*/}
        {/*            'addressById',*/}
        {/*            'street',*/}
        {/*            e?.component?.value*/}
        {/*          )*/}
        {/*        }*/}
        {/*      />*/}
        {/*    </Col>*/}
        {/*  </Row>*/}
        {/*  <Row className="g-2 gx-5">*/}
        {/*    <Col className="col-12 col-md-6">*/}
        {/*      <InputField*/}
        {/*        label={t('HOME_NUMBER')}*/}
        {/*        name={stepOneFields.home}*/}
        {/*        isDisabled={!!disabledFields?.[stepOneFields.home]}*/}
        {/*        value={personalInfo.addressById.home}*/}
        {/*        isRequired*/}
        {/*        maxLength={10}*/}
        {/*        onChange={(e) =>*/}
        {/*          handleChangeAddress(*/}
        {/*            'addressById',*/}
        {/*            'home',*/}
        {/*            e?.component?.value*/}
        {/*          )*/}
        {/*        }*/}
        {/*      />*/}
        {/*    </Col>*/}
        {/*    <Col className="col-12 col-md-6">*/}
        {/*      <InputField*/}
        {/*        label={t('APARTMENT_NUMBER')}*/}
        {/*        name={stepOneFields.apartment}*/}
        {/*        isDisabled={!!disabledFields?.[stepOneFields.apartment]}*/}
        {/*        value={personalInfo.addressById.apartment}*/}
        {/*        maxLength={10}*/}
        {/*        onChange={(e) =>*/}
        {/*          handleChangeAddress(*/}
        {/*            'addressById',*/}
        {/*            'apartment',*/}
        {/*            e?.component?.value*/}
        {/*          )*/}
        {/*        }*/}
        {/*      />*/}
        {/*    </Col>*/}
        {/*  </Row>*/}
        {/*</Collapse>*/}

        {/*<CollapseHeader*/}
        {/*  id="4"*/}
        {/*  isOpen={collapses['4']}*/}
        {/*  title={t('ADDRESS_FOR_STUDYING')}*/}
        {/*  toggle={() => toggleCollapse('4')}*/}
        {/*/>*/}
        {/*<Collapse isOpen={collapses['4']}>*/}
        {/*  <Row className="mb-8">*/}
        {/*    <Col className="col-12 mb-40">*/}
        {/*      <CheckBox*/}
        {/*        value={personalInfo.addressesMatch}*/}
        {/*        name={stepOneFields.matchAddresses}*/}
        {/*        isDisabled={!!disabledFields?.[stepOneFields.matchAddresses]}*/}
        {/*        form={config.forms.personalData}*/}
        {/*        onChange={() =>*/}
        {/*          updatePersonalInfo({*/}
        {/*            changes: {addressesMatch: !personalInfo.addressesMatch},*/}
        {/*          })*/}
        {/*        }*/}
        {/*      >*/}
        {/*        {t('ADDRESSES_ARE_SAME')}*/}
        {/*      </CheckBox>*/}
        {/*    </Col>*/}
        {/*  </Row>*/}
        {/*  <Row className="g-2 gx-5">*/}
        {/*    <Col className="col-12 col-md-6">*/}
        {/*      <CUI.Label _labelPerach>{t('CITY')}</CUI.Label>*/}
        {/*      <CUI.DropDownSelect*/}
        {/*        form={config.forms.personalData}*/}
        {/*        name={stepOneFields.addressWhileStudying}*/}
        {/*        isDisabled={*/}
        {/*          !!disabledFields?.[stepOneFields.addressWhileStudying]*/}
        {/*        }*/}
        {/*        value={fixValue(personalInfo?.addressWhileStudying?.city, cities) || null}*/}
        {/*        data={cities}*/}
        {/*        textField={'text'}*/}
        {/*        shouldAllowEmpty*/}
        {/*        shouldFilterValues*/}
        {/*        onChange={(e) =>*/}
        {/*          handleChangeAddress(*/}
        {/*            'addressWhileStudying',*/}
        {/*            'city',*/}
        {/*            e?.component?.value*/}
        {/*          )*/}
        {/*        }*/}
        {/*        isRequired*/}
        {/*        requiredMessage={t('FIELD_REQUIRED')}*/}

        {/*      />*/}
        {/*    </Col>*/}
        {/*    <Col className="col-12 col-md-6">*/}
        {/*      <InputField*/}
        {/*        label={t('STREET')}*/}
        {/*        name={stepOneFields.streetWhileStudying}*/}
        {/*        isDisabled={*/}
        {/*          !!disabledFields?.[stepOneFields.streetWhileStudying]*/}
        {/*        }*/}
        {/*        value={personalInfo.addressWhileStudying.street}*/}
        {/*        isRequired*/}
        {/*        maxLength={30}*/}
        {/*        onChange={(e) =>*/}
        {/*          handleChangeAddress(*/}
        {/*            'addressWhileStudying',*/}
        {/*            'street',*/}
        {/*            e?.component?.value*/}
        {/*          )*/}
        {/*        }*/}
        {/*      />*/}
        {/*    </Col>*/}
        {/*  </Row>*/}
        {/*  <Row className="g-2 gx-5">*/}
        {/*    <Col className="col-12 col-md-6">*/}
        {/*      <InputField*/}
        {/*        label={t('HOME_NUMBER')}*/}
        {/*        name={stepOneFields.homeWhileStudying}*/}
        {/*        isDisabled={!!disabledFields?.[stepOneFields.homeWhileStudying]}*/}
        {/*        value={personalInfo.addressWhileStudying.home}*/}
        {/*        isRequired*/}
        {/*        maxLength={10}*/}
        {/*        onChange={(e) =>*/}
        {/*          handleChangeAddress(*/}
        {/*            'addressWhileStudying',*/}
        {/*            'home',*/}
        {/*            e?.component?.value*/}
        {/*          )*/}
        {/*        }*/}
        {/*      />*/}
        {/*    </Col>*/}
        {/*    <Col className="col-12 col-md-6">*/}
        {/*      <InputField*/}
        {/*        label={t('APARTMENT_NUMBER')}*/}
        {/*        name={stepOneFields.apartmentWhileStudying}*/}
        {/*        isDisabled={*/}
        {/*          !!disabledFields?.[stepOneFields.apartmentWhileStudying]*/}
        {/*        }*/}
        {/*        value={personalInfo.addressWhileStudying.apartment}*/}
        {/*        maxLength={10}*/}
        {/*        onChange={(e) =>*/}
        {/*          handleChangeAddress(*/}
        {/*            'addressWhileStudying',*/}
        {/*            'apartment',*/}
        {/*            e?.component?.value*/}
        {/*          )*/}
        {/*        }*/}
        {/*      />*/}
        {/*    </Col>*/}
        {/*  </Row>*/}
        {/*</Collapse>*/}

        {/*NEW FIELDS*/}
        <CollapseHeader
          id="7"
          isOpen={collapses['7']}
          title={t('ADDRESS_BY_ID')}
          // title={t('STEP_ONE_FIELDS.AGE_18_ADDRESS_COLLAPSE')}
          toggle={() => toggleCollapse('7')}
        />
        <Collapse isOpen={collapses['7']}>
          {/*<Row className="mb-16">*/}
          {/*  <Col className="col-12">*/}
          {/*    <CheckBox*/}
          {/*      value={personalInfo.isUnder18address}*/}
          {/*      checked={!!personalInfo?.isUnder18address}*/}
          {/*      name={stepOneFields.isUnder18address}*/}
          {/*      isDisabled={!!disabledFields?.[stepOneFields.isUnder18address]}*/}
          {/*      form={config.forms.personalData}*/}
          {/*      onChange={() =>*/}
          {/*        updatePersonalInfo({*/}
          {/*          changes: {*/}
          {/*            isUnder18address: !personalInfo.isUnder18address,*/}
          {/*          },*/}
          {/*        })*/}
          {/*      }*/}
          {/*    >*/}
          {/*      {t('STEP_ONE_FIELDS.UNDER_18_OUTSIDE_ISRAEL_CHECKBOX')}*/}
          {/*    </CheckBox>*/}
          {/*  </Col>*/}
          {/*</Row>*/}
          <Row className="g-2 gx-5 mb-40">
            <Col className="col-12 col-md-6">
              <LabelWithPopup
                label={t('CITY')}
                helpKey={stepOneFields.under18City}
              />
              <CUI.DropDownSelect
                form={config.forms.personalData}
                name={stepOneFields.under18City}
                isDisabled={
                  !!personalInfo?.isUnder18address ||
                  !!disabledFields?.[stepOneFields.under18City]
                }
                value={fixValue(personalInfo?.under18City, cities)}
                data={cities}
                textField={'text'}
                shouldAllowEmpty
                shouldFilterValues
                onChange={(e) =>
                  updatePersonalInfo({
                    changes: {
                      under18City: e.component.value,
                      // under18District: { value: '', text: '' },
                    },
                  })
                }
                isRequired={!personalInfo?.isUnder18address}
              />
            </Col>
            {/* <Col>
              <LabelWithPopup
                label={t('STEP_ONE_FIELDS.DISTRICT_DROPDOWN')}
                helpKey={stepOneFields.under18District}
              />
              <CUI.DropDownSelect
                form={config.forms.personalData}
                name={stepOneFields.under18District}
                isDisabled={
                  !!personalInfo?.isUnder18address ||
                  !!disabledFields?.[stepOneFields.under18District]
                }
                value={fixValue(personalInfo?.under18District, [
                  ...districts,
                  // { value: '9999', text: 'None' },
                ])}
                data={[
                  ...districts,
                  // { value: '9999', text: 'None' }
                ]}
                isLoading={loaders?.[Dictionaries.Districts] || false}
                textField={'text'}
                shouldAllowEmpty
                shouldFilterValues
                onChange={(e) =>
                  updatePersonalInfo({
                    changes: { under18District: e.component.value },
                  })
                }
                isRequired={!personalInfo?.isUnder18address}
              />
              <CUI.Span>אם לא מצאת, יש לבחור אחר</CUI.Span>
            </Col> */}
          </Row>
          {/* <Row className="g-2 gx-5">
            <Col className="col-12 col-md-6">
              <InputField
                label={t('STREET')}
                name={stepOneFields.under18Street}
                isDisabled={
                  !!personalInfo?.isUnder18address ||
                  !!disabledFields?.[stepOneFields.under18Street]
                }
                value={personalInfo.under18Street}
                maxLength={30}
                onChange={(e) =>
                  updatePersonalInfo({
                    changes: { under18Street: e.component.value },
                  })
                }
                isRequired={!personalInfo?.isUnder18address}
              />
            </Col>
            <Col className="col-12 col-md-6">
              <InputField
                label={t('HOME_NUMBER')}
                name={stepOneFields.under18Home}
                isDisabled={
                  !!personalInfo?.isUnder18address ||
                  !!disabledFields?.[stepOneFields.under18Home]
                }
                value={personalInfo.under18Home}
                isRequired={!personalInfo?.isUnder18address}
                maxLength={10}
                onChange={(e) =>
                  updatePersonalInfo({
                    changes: { under18Home: e.component.value },
                  })
                }
              />
            </Col>
          </Row> */}
          {/* <Row className="mb-8">
           <Col className="col-12">
             <CheckBox
                value={personalInfo.neighborhoodConfirmCheckbox}
                name={stepOneFields.neighborhoodConfirmCheckbox}
                checked={!!personalInfo?.neighborhoodConfirmCheckbox}
                isDisabled={
                  !!disabledFields?.[stepOneFields.neighborhoodConfirmCheckbox]
                }
                form={config.forms.personalData}
                onChange={() =>
                  updatePersonalInfo({
                    changes: {neighborhoodConfirmCheckbox: !personalInfo.neighborhoodConfirmCheckbox},
                  })
                }
              >
                {t('STEP_ONE_FIELDS.NEIGHBORHOOD_CONFIRM_CHECKBOX')}
            </CheckBox>
           </Col>
          </Row> */}
          {/*<Row className="mb-8">*/}
          {/*  <Col className="col-12">*/}
          {/*    <CheckBox*/}
          {/*      value={personalInfo.under18Confirm}*/}
          {/*      name={stepOneFields.under18Confirm}*/}
          {/*      checked={!!personalInfo?.under18Confirm}*/}
          {/*      isDisabled={*/}
          {/*        !!disabledFields?.[stepOneFields.under18Confirm]*/}
          {/*      }*/}
          {/*      form={config.forms.personalData}*/}
          {/*      onChange={() =>*/}
          {/*        updatePersonalInfo({*/}
          {/*          changes: {under18Confirm: !personalInfo.under18Confirm},*/}
          {/*        })*/}
          {/*      }*/}
          {/*    >*/}
          {/*      {t('STEP_ONE_FIELDS.UNDER_18_CONFIRM_CHECKBOX')}*/}
          {/*    </CheckBox>*/}
          {/*  </Col>*/}
          {/*</Row>*/}
        </Collapse>
        {/*NEW FIELDS ENDS*/}

        <CollapseHeader
          id="5"
          isOpen={collapses['5']}
          title={t('ACADEMIC_INFO')}
          toggle={() => toggleCollapse('5')}
        />
        <Collapse isOpen={collapses['5']}>
          <Row className="g-2 gx-5 mb-40">
            <Col className="col-12 col-md-6">
              <LabelWithPopup
                label={t('DEGREE_TYPE')}
                helpKey={stepOneFields.degreeType}
              />
              <CUI.DropDownSelect
                form={config.forms.personalData}
                name={stepOneFields.degreeType}
                isDisabled={!!disabledFields?.[stepOneFields.degreeType]}
                value={fixValue(
                  personalInfo?.educationDetails?.degreeType,
                  degreeTypeMock
                )}
                data={degreeTypeMock}
                textField={'text'}
                shouldAllowEmpty
                shouldFilterValues
                onChange={(e) => {
                  const degreeValue = e?.component?.value;
                  updatePersonalInfo({
                    changes: {
                      educationDetails: {
                        ...personalInfo.educationDetails,
                        degreeType: degreeValue,
                        isBachelor: degreeValue?.value === DegreeType.BACHELOR,
                      },
                      masterTrackType:
                        degreeValue?.value === DegreeType.MASTER
                          ? personalInfo.masterTrackType
                          : { text: '', value: '' },
                    },
                  });
                }}
                isRequired
              />
            </Col>
          </Row>
          <Row className="g-2 gx-5">
            <Col className="col-12 col-md-6">
              <InputField
                label={t('SCHOOL')}
                name={stepOneFields.school}
                isDisabled={!!disabledFields?.[stepOneFields.school]}
                value={personalInfo?.educationDetails?.school || ""}
                isRequired
              />
            </Col>
          </Row>
          <Row className="g-2 gx-5">
            <Col className="col-12 col-md-6 mb-40">
              <LabelWithPopup
                label={t('YEAR_OF_STUDYING')}
                helpKey={stepOneFields.year}
              />
              <CUI.DropDownSelect
                form={config.forms.personalData}
                name={stepOneFields.year}
                isDisabled={!!disabledFields?.[stepOneFields.year]}
                value={fixValue(
                  personalInfo?.educationDetails.year,
                  schoolYearMock
                )}
                data={schoolYearMock}
                textField={'text'}
                shouldAllowEmpty
                shouldFilterValues
                onChange={(e) => {
                  const yearValue = e?.component?.value;
                  updatePersonalInfo({
                    changes: {
                      educationDetails: {
                        ...personalInfo.educationDetails,
                        year: yearValue,
                      },
                      studyStartSemester:
                        yearValue?.value === 'SECOND_YEAR'
                          ? personalInfo.studyStartSemester
                          : { text: '', value: '' },
                    },
                  });
                }}
                isRequired
              />
            </Col>
          </Row>
          <Optional
            when={personalInfo?.educationDetails?.year?.value === 'SECOND_YEAR'}
          >
            <Row className="g-2 gx-5">
              <Col className="col-12 col-md-6 mb-40">
                <LabelWithPopup
                  label={t('STUDY_START_QUESTION')}
                  helpKey={stepOneFields.studyStartSemester}
                />
                <CUI.DropDownSelect
                  form={config.forms.personalData}
                  name={stepOneFields.studyStartSemester}
                  isDisabled={
                    !!disabledFields?.[stepOneFields.studyStartSemester]
                  }
                  value={fixValue(
                    personalInfo?.studyStartSemester,
                    studyStartSemesterMock
                  )}
                  data={studyStartSemesterMock}
                  textField={'text'}
                  shouldAllowEmpty
                  shouldFilterValues
                  onChange={(e) =>
                    updatePersonalInfo({
                      changes: { studyStartSemester: e.component.value },
                    })
                  }
                  isRequired
                />
              </Col>
            </Row>
          </Optional>
          <Optional
            when={
              personalInfo?.educationDetails?.degreeType?.value ===
              DegreeType.MASTER
            }
          >
            <Row className="g-2 gx-5">
              <Col className="col-12 col-md-6 mb-40">
                <LabelWithPopup
                  label={t('MASTER_TRACK_TYPE')}
                  helpKey={stepOneFields.masterTrackType}
                />
                <CUI.DropDownSelect
                  form={config.forms.personalData}
                  name={stepOneFields.masterTrackType}
                  isDisabled={
                    !!disabledFields?.[stepOneFields.masterTrackType]
                  }
                  value={fixValue(personalInfo?.masterTrackType, masterTrackMock)}
                  data={masterTrackMock}
                  textField={'text'}
                  shouldAllowEmpty
                  shouldFilterValues
                  onChange={(e) =>
                    updatePersonalInfo({
                      changes: { masterTrackType: e.component.value },
                    })
                  }
                  isRequired
                />
              </Col>
            </Row>
          </Optional>
          <Row className="g-2 gx-5">
            <Col className="col-12 col-md-6 mb-40">
              <LabelWithPopup
                label={t('SPECIALITY')}
                helpKey={stepOneFields.field}
              />
              <CUI.DropDownSelect
                form={config.forms.personalData}
                name={stepOneFields.field}
                isDisabled={!!disabledFields?.[stepOneFields.field]}
                value={fixValue(
                  personalInfo?.educationDetails.field,
                  studyFields
                )}
                data={studyFields}
                textField={'text'}
                shouldAllowEmpty
                shouldFilterValues
                onChange={(e) =>
                  handleChangeEducation('field', e?.component?.value)
                }
                isRequired
              />
            </Col>
            <Optional
              when={personalInfo?.educationDetails.field.value === '17'}
            >
              <Col className="col-12 col-md-6">
                <InputField
                  label={t('WHAT_SPECIALITY')}
                  name={stepOneFields.otherField}
                  isDisabled={!!disabledFields?.[stepOneFields.otherField]}
                  value={personalInfo.educationDetails.otherField}
                  onChange={(e) =>
                    handleChangeEducation('otherField', e?.component?.value)
                  }
                  maxLength={30}
                  isRequired={true}
                  requiredMessage={t('FIELD_REQUIRED')}
                />
              </Col>
            </Optional>
          </Row>
          <Row className="g-2 gx-5 mb-16">
            <Col className="col-12 col-md-6">
              <LabelWithPopup
                label={t('ADDITIONAL_SPECIALITY')}
                helpKey={stepOneFields.additionalField}
              />
              <CUI.DropDownSelect
                form={config.forms.personalData}
                name={stepOneFields.additionalField}
                isDisabled={!!disabledFields?.[stepOneFields.additionalField]}
                value={fixValue(
                  personalInfo?.educationDetails.additionalField,
                  [{ value: '', text: 'ללא' }, ...studyFields]
                )}
                data={[{ value: '', text: 'ללא' }, ...studyFields]}
                textField={'text'}
                shouldAllowEmpty
                shouldFilterValues
                onChange={(e) =>
                  handleChangeEducation('additionalField', e?.component?.value)
                }
              />
            </Col>
            <Optional
              when={
                personalInfo?.educationDetails.additionalField.value === '17'
              }
            >
              <Col className="col-12 col-md-6">
                <InputField
                  label={t('WHAT_SPECIALITY')}
                  name={stepOneFields.additionalAnotherFieldOfStudy}
                  isDisabled={
                    !!disabledFields?.[
                      stepOneFields.additionalAnotherFieldOfStudy
                    ]
                  }
                  value={
                    personalInfo?.educationDetails
                      ?.additionalAnotherFieldOfStudy
                  }
                  onChange={(e) =>
                    handleChangeEducation(
                      'additionalAnotherFieldOfStudy',
                      e?.component?.value
                    )
                  }
                  maxLength={30}
                  requiredMessage={t('FIELD_REQUIRED')}
                  isRequired={true}
                />
              </Col>
            </Optional>
          </Row>
          {/* <Row className="mb-8"> */}
            {/* <Col className="col-12 d-inline-flex align-items-center">
              <CheckBox
                value={personalInfo.receivedAchievementsAdvising}
                name={stepOneFields.receivedAchievementsAdvising}
                checked={!!personalInfo?.receivedAchievementsAdvising}
                isDisabled={
                  !!disabledFields?.[stepOneFields.receivedAchievementsAdvising]
                }
                form={config.forms.personalData}
                onChange={() =>
                  updatePersonalInfo({
                    changes: {
                      receivedAchievementsAdvising:
                        !personalInfo.receivedAchievementsAdvising,
                    },
                  })
                }
              >
                {t('ACHIEVEMENTS_PROGRAM_ADVISING')}
              </CheckBox>
              <span className={'strong-text-important'}>
                <HelpText
                  fieldName={
                    stepOneFields.receivedAchievementsAdvising as HelpKeyType
                  }
                />
              </span>
            </Col> */}
          {/* </Row> */}
        </Collapse>

        <Optional when={!shouldHideButtons}>
          <Row>
            <Col className="col-12 col-md-9 mb-40">
              {/* <OpionalTooltip */}
                {/* option={isButtonDisabled} */}
                {/* text={t('DISABLE_BUTTON_STEP_ONE_TOOLTIP')} */}
              {/* > */}
                <CUI.Button
                  _dBlock
                  _w100
                  _signInButton
                  _btnPrimaryPerach
                  form={config.forms.personalData}
                  isDisabled={
                    /*isButtonDisabled ||*/
                    !!disabledFields?.[stepOneFields.buttonSubmit]
                  }
                  shouldScrollToInvalidFields
                  scrollOffset={135}
                  onClick={() => saveUserPersonalData()}
                >
                  {t('NEXT')}
                </CUI.Button>
              {/* </OpionalTooltip> */}
            </Col>
            <Col className="col-12 col-md-3 mb-40">
              <CUI.Button
                _dBlock
                _w100
                _signInButton
                _btnInfoPerach
                isDisabled={!!disabledFields?.[stepOneFields.buttonSave]}
                onClick={() => {
                  setIsModalOpen(true);
                }}
              >
                {t('SAVE_AND_CLOSE')}
              </CUI.Button>
            </Col>
          </Row>
        </Optional>
      </div>
    </>
  );
};
